import { com } from '@eidu/web'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import EqualityHashMap from '../util/EqualityHashMap'
import useStableEqualityHashMap from '../util/useStableEqualityHashMap'
import EntityWithLabelAndRelated from '../domain/entity/EntityWithLabelAndRelated'
import fetchAllEntitiesOfType from './fetchAllEntitiesOfType'
import equalityHashMapStructuralSharing from './equalityHashMapStructuralSharing'
import { entityKeys, entityTypeKeys } from './queryKeys'
import { fetchAllEntityTypes } from './useEntityTypes'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType

const DEFAULT_PAGE_SIZE = 100

type UseEntitiesOfTypeParams = {
  typeIds: readonly EntityTypeId[]
  searchQuery?: string
  allTypes: ReadonlyMap<EntityTypeId, EntityType | null>
  pageSize?: number
}

const useEntitiesOfType = ({
  typeIds,
  searchQuery,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseEntitiesOfTypeParams):
  | ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
  | undefined => {
  const queryClient = useQueryClient()
  const queries = useQueries({
    queries: typeIds.map((typeId) => ({
      queryKey: [...entityKeys.ofType(typeId), 'all', searchQuery ?? '', pageSize],
      queryFn: async () => {
        const types = await queryClient.fetchQuery({
          queryKey: entityTypeKeys.list(),
          queryFn: fetchAllEntityTypes,
        })
        return fetchAllEntitiesOfType(typeId, pageSize, searchQuery, types)
      },
      structuralSharing: equalityHashMapStructuralSharing,
    })),
  })

  const allReady = queries.every((q) => q.data !== undefined)
  const data = useMemo(
    () =>
      new EqualityHashMap(
        typeIds.map((typeId, i) => [typeId, queries[i].data as ReadonlyMap<EntityId, EntityWithLabelAndRelated>])
      ),
    [typeIds, queries]
  )
  const stable = useStableEqualityHashMap(data)
  return allReady ? stable : undefined
}

export default useEntitiesOfType
